import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateTenant } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import { getClinicPortalLinks } from "../../../utils/clinicPortalLinks";
import AdminPatientImport from "../AdminPatients/AdminPatientImport";
import SettingsPortalBrandingPanel from "./SettingsPortalBrandingPanel";
import {
  Button,
  Field,
  Input,
  SkeletonCard,
  Tabs,
  Textarea,
  useToast,
} from "../../ui";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { resetOnboarding } from "../AdminOnboarding/onboardingStorage";
import { HubFormCard, HubScreen, HubSegment } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";

const DEFAULT = {
  name: "",
  address: "",
  phone: "",
  logo_url: "",
  subdomain: "",
  status: "",
  timezone: "Europe/Moscow",
  slot_step_minutes: 15,
  slot_buffer_minutes: 0,
  min_booking_lead_hours: 2,
  cancellation_lead_hours: 24,
  patient_reminder_enabled: true,
  auto_approve_registrations: true,
  portal_accent_color: null,
  portal_cover_url: null,
  portal_font_family: null,
  portal_description: "",
  portal_gallery: [],
};

const STATUS_LABELS = {
  trial: "Пробный",
  active: "Активна",
  suspended: "Приостановлена",
};

const TABS = [
  { id: "general", label: "Общие" },
  { id: "portal", label: "Портал" },
  { id: "notify", label: "Уведомления" },
  { id: "data", label: "Данные" },
];

/** Вкладки PWA-настроек (те же подписи, что на десктопе). */
const APP_TABS = TABS;

const REMOVED_TABS = new Set([
  "structure",
  "team",
  "booking",
  "venues",
  "directions",
  "registration",
]);

export default function AdminSettings() {
  const toast = useToast();
  const dispatch = useDispatch();
  const isApp = useAdminHubUi();
  const { tenant: authTenant } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const legacyTab = REMOVED_TABS.has(tabFromUrl) ? "general" : tabFromUrl;
  const validTab = TABS.some((t) => t.id === legacyTab) ? legacyTab : "general";
  const [tab, setTab] = useState(validTab);

  useEffect(() => {
    if (validTab !== tab) setTab(validTab);
    if (tabFromUrl && REMOVED_TABS.has(tabFromUrl)) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [validTab, tab, tabFromUrl, searchParams, setSearchParams]);

  const setTabAndUrl = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    if (id === "general") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    clinicAPI
      .getTenantSettings()
      .then((r) => {
        if (r.tenant) setForm({ ...DEFAULT, ...r.tenant });
      })
      .catch((e) => toast.error(e.response?.data?.error || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [toast]);

  const buildPayload = () => {
    const payload = { ...form };
    delete payload.subdomain;
    delete payload.status;
    delete payload.id;
    return payload;
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await clinicAPI.updateTenantSettings(buildPayload());
      setForm({ ...DEFAULT, ...r.tenant });
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const portalLinks = getClinicPortalLinks(
    form.subdomain || authTenant?.subdomain,
  );

  const restartOnboarding = () => {
    if (!authTenant?.id) return;
    resetOnboarding(authTenant.id);
    toast.success("Мастер настройки снова откроется на сводке");
    window.location.href = "/manage/app";
  };

  const mergeTenant = (tenant) => {
    if (!tenant) return;
    setForm((f) => ({ ...f, ...tenant }));
    dispatch(
      updateTenant({
        portal_accent_color: tenant.portal_accent_color ?? null,
        portal_cover_url: tenant.portal_cover_url ?? null,
        portal_font_family: tenant.portal_font_family ?? null,
        portal_description: tenant.portal_description ?? null,
        portal_gallery: tenant.portal_gallery ?? [],
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        phone: tenant.phone,
      }),
    );
  };

  const saveButton = (fullWidth = false) => (
    <div className={isApp ? aa.settingsHubSaveRow : undefined}>
      <Button
        onClick={save}
        disabled={saving}
        className={fullWidth ? aa.hubPrimaryCta : undefined}
        style={fullWidth ? { width: "100%" } : undefined}
      >
        {saving ? "Сохранение…" : "Сохранить"}
      </Button>
    </div>
  );

  const generalFields = (
    <>
      <Field label="Название клуба">
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </Field>
      <Field label="Телефон">
        <Input
          value={form.phone || ""}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </Field>
      <Field label="Адрес">
        <Input
          value={form.address || ""}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </Field>
      <Field label="О клубе">
        <Textarea
          value={form.portal_description || ""}
          onChange={(e) =>
            setForm({ ...form, portal_description: e.target.value })
          }
          rows={4}
          placeholder="Краткое описание для клиентов: направления, атмосфера, как добраться"
        />
      </Field>
      {!isApp ? (
        <Field label="URL логотипа">
          <Input
            value={form.logo_url || ""}
            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
          />
        </Field>
      ) : null}
    </>
  );

  const portalLinksBlock =
    form.subdomain && tab === "general" && !isApp ? (
      <div className={isApp ? aa.hubListGroup : md.desktopSettingsPortalGrid}>
        {portalLinks.map((link) => (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={isApp ? aa.hubLinkRow : s.settingsPortalTile}
          >
            <strong>{link.title}</strong>
            {isApp ? null : (
              <code className={s.settingsPortalUrl}>{link.url}</code>
            )}
          </a>
        ))}
      </div>
    ) : null;

  const panelClass = isApp ? s.settingsPanel : md.desktopSettingsSection;

  const tabPanels = (
    <>
      {tab === "general" && (
        <form onSubmit={save} className={panelClass}>
          {isApp ? (
            <HubFormCard title="Клуб">{generalFields}</HubFormCard>
          ) : (
            <div className={md.desktopSettingsFormGrid}>{generalFields}</div>
          )}
          {portalLinksBlock}
          {saveButton(isApp)}
        </form>
      )}

      {tab === "portal" && (
        <SettingsPortalBrandingPanel
          accentColor={form.portal_accent_color}
          coverUrl={form.portal_cover_url}
          logoUrl={form.logo_url}
          fontFamily={form.portal_font_family}
          galleryUrls={form.portal_gallery}
          onUpdated={mergeTenant}
          compact={isApp}
          rootClassName={!isApp ? md.desktopSettingsBrandPage : ""}
        />
      )}

      {tab === "notify" && (
        <form onSubmit={save} className={panelClass}>
          {isApp ? (
            <HubFormCard>
              <label className={s.settingsToggleRow}>
                <input
                  type="checkbox"
                  checked={form.patient_reminder_enabled !== false}
                  onChange={(e) =>
                    setForm({ ...form, patient_reminder_enabled: e.target.checked })
                  }
                />
                <span>
                  <strong>Email-напоминания клиентам</strong>
                  <span className={s.branchCheckAddr}>
                    Накануне занятия в 18:00
                  </span>
                </span>
              </label>
            </HubFormCard>
          ) : (
            <>
            <label className={s.settingsToggleRow}>
              <input
                type="checkbox"
                checked={form.patient_reminder_enabled !== false}
                onChange={(e) =>
                  setForm({ ...form, patient_reminder_enabled: e.target.checked })
                }
              />
              <span>
                <strong>Email-напоминания клиентам</strong>
                <span className={s.branchCheckAddr}>
                  Отправка накануне занятия в 18:00 (нужен email клиента)
                </span>
              </span>
            </label>
            </>
          )}
          <p className={isApp ? aa.hubHint : s.docSectionHint}>
            Напоминания тренерам — в профиле каждого тренера.
          </p>
          {saveButton(isApp)}
        </form>
      )}

      {tab === "data" && (
        <div className={panelClass}>
          {isApp ? (
            <div className={aa.hubListGroup}>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={() => setImportOpen(true)}
              >
                <span>
                  <strong>Импорт клиентов</strong>
                  <span className={aa.hubHint}>CSV: full_name, email, phone…</span>
                </span>
              </button>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={async () => {
                  try {
                    const res = await clinicAPI.exportPatientsCsv();
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "patients.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("CSV скачан");
                  } catch (e) {
                    toast.error(e.response?.data?.error || "Ошибка");
                  }
                }}
              >
                <span>
                  <strong>Экспорт клиентов</strong>
                  <span className={aa.hubHint}>Скачать реестр CSV</span>
                </span>
              </button>
              <Link to="/manage/app/clients" className={aa.hubLinkRow}>
                <span>
                  <strong>Реестр клиентов</strong>
                  <span className={aa.hubHint}>Поиск и карточки</span>
                </span>
              </Link>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={restartOnboarding}
              >
                <span>
                  <strong>Первичная настройка</strong>
                  <span className={aa.hubHint}>Открыть мастер заново</span>
                </span>
              </button>
            </div>
          ) : (
            <div className={md.desktopSettingsDataGrid}>
              <div className={s.settingsDataCard}>
                <strong>Импорт клиентов</strong>
                <p className={s.docSectionHint}>
                  Загрузка CSV с колонками full_name, email, phone и др.
                </p>
                <Button type="button" onClick={() => setImportOpen(true)}>
                  Импорт CSV
                </Button>
              </div>
              <div className={s.settingsDataCard}>
                <strong>Экспорт клиентов</strong>
                <p className={s.docSectionHint}>
                  Скачать реестр для редактирования и повторного импорта
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={async () => {
                    try {
                      const res = await clinicAPI.exportPatientsCsv();
                      const url = URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "patients.csv";
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("CSV скачан");
                    } catch (e) {
                      toast.error(e.response?.data?.error || "Ошибка");
                    }
                  }}
                >
                  Скачать CSV
                </Button>
              </div>
              <Link to="/manage/app/clients" className={s.settingsDataCard}>
                <strong>Реестр клиентов</strong>
                <span className={s.docSectionHint}>Поиск, дубликаты, карточки</span>
              </Link>
              <div className={s.settingsDataCard}>
                <strong>Первичная настройка</strong>
                <p className={s.docSectionHint}>
                  Заново пройти мастер: секция, залы, группы, клиент
                </p>
                <Button type="button" variant="secondary" onClick={restartOnboarding}>
                  Открыть мастер
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (tabFromUrl === "registration") {
    return <Navigate to="/manage/app/clients/registration" replace />;
  }

  if (loading) {
    const skeleton = <SkeletonCard />;
    return isApp ? (
      <HubScreen title="Настройки">
        {skeleton}
      </HubScreen>
    ) : (
      <div className={md.desktopSettingsPage}>{skeleton}</div>
    );
  }

  if (isApp) {
    return (
      <HubScreen title="Настройки">
        <div className={aa.settingsHubClubRow}>
          {form.logo_url ? (
            <img src={form.logo_url} alt="" className={aa.settingsHubClubLogo} />
          ) : (
            <div className={aa.settingsHubClubLogoPh}>
              {(form.name || "К").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className={aa.settingsHubClubMeta}>
            <strong>{form.name || "Клуб"}</strong>
            <span>
              {form.subdomain ? `${form.subdomain} · ` : ""}
              {STATUS_LABELS[form.status] || form.status || "—"}
            </span>
          </div>
        </div>

        <HubSegment tabs={APP_TABS} active={tab} onChange={setTabAndUrl} />
        {tabPanels}

        <AdminPatientImport
          open={importOpen}
          onClose={() => setImportOpen(false)}
          appMode={isApp}
        />
      </HubScreen>
    );
  }

  return (
    <div className={md.desktopSettingsPage}>
      <div className={md.desktopSettingsPanel}>
        <header className={md.desktopSettingsHead}>
          <div className={md.desktopSettingsClub}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" className={md.desktopSettingsClubLogo} />
            ) : (
              <div className={md.desktopSettingsClubLogoPh}>
                {(form.name || "К").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className={md.desktopSettingsClubText}>
              <p className={md.desktopSettingsClubName}>{form.name || "Клуб"}</p>
              <p className={md.desktopSettingsClubMeta}>
                {form.subdomain ? `${form.subdomain} · ` : ""}
                {STATUS_LABELS[form.status] || form.status || "—"}
                {form.phone ? ` · ${form.phone}` : ""}
              </p>
            </div>
          </div>
          <Button onClick={save} disabled={saving}>
            {saving ? "Сохранение…" : "Сохранить всё"}
          </Button>
        </header>

        <div className={md.desktopSettingsTabs}>
          <Tabs items={TABS} active={tab} onChange={setTabAndUrl} />
        </div>

        <div className={md.desktopSettingsBody}>{tabPanels}</div>
      </div>

      <AdminPatientImport open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
